import React, { useCallback, useId } from 'react';
import { ChangeHandler, useController } from 'react-hook-form';
import { t } from '@lingui/macro';

import SENSITIVE_DATA_CLASS from '@rover/react-lib/src/constants/sensitiveData.constants';

import { Flex } from '../../../core';
import { FeedbackMessage, PrimaryLabel, SecondaryLabel } from '../sharedTypography';
import { getAriaDescribedByString, stopPropagation } from '../utils';

import { CheckboxFieldCommonProps } from './CheckboxField.common';
import CheckboxFieldOption from './CheckboxFieldOption';

export type Props = CheckboxFieldCommonProps;

const CheckboxField = ({
  control,
  disabled,
  fieldError,
  fieldProps,
  helperMessage,
  hideRequiredAsterisk = false,
  name,
  primaryLabel,
  primaryLabelA11yHidden,
  primaryLabelSize,
  required = true,
  secondaryLabel,
  sensitive = false,
  onChange = async () => {},
  options,
  variant = 'default',
}: Props): JSX.Element => {
  const { field } = useController({ control, defaultValue: '', name });

  const primaryLabelId = useId();
  const fieldErrorId = useId();
  const helperMessageId = useId();
  const secondaryLabelId = useId();

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const updatedValue = field.value.includes(value)
        ? field.value.filter((val) => val !== value)
        : [...field.value, value];
      field.onChange(updatedValue);
      onChange({ webEvent: event, values: updatedValue });
    },
    [field, onChange]
  );

  return (
    <Flex
      className={sensitive ? SENSITIVE_DATA_CLASS : undefined}
      as="fieldset"
      flexDirection="column"
      mb="6x"
      p="0x" // remove default fieldset padding
      border="none"
      aria-describedby={getAriaDescribedByString([
        [fieldError, fieldErrorId],
        [secondaryLabel, secondaryLabelId],
        [helperMessage, helperMessageId],
      ])}
      aria-disabled={disabled}
      aria-invalid={fieldError ? 'true' : 'false'}
      aria-label={
        required
          ? t({
              message: `required, ${primaryLabel}`,
              comment: 'Prepending "required" to the primary label for screen readers',
            })
          : undefined
      }
    >
      <PrimaryLabel
        as="legend"
        required={required}
        primaryLabel={primaryLabel}
        primaryLabelA11yHidden={primaryLabelA11yHidden}
        primaryLabelSize={primaryLabelSize}
        hideRequiredAsterisk={hideRequiredAsterisk}
      />
      <SecondaryLabel secondaryLabel={secondaryLabel} labelId={secondaryLabelId} />
      <Flex
        flexDirection="column"
        data-testid="kff-checkbox-input-flex-parent"
        onClick={stopPropagation}
      >
        {options.map((option) => (
          <CheckboxFieldOption
            // the `as` here is fine, there is just a small mismatch between the types
            fieldProps={{ ...fieldProps, onChange: handleOnChange as ChangeHandler }}
            key={option.value}
            primaryLabel={option.primaryLabel}
            secondaryLabel={option.secondaryLabel}
            value={option.value}
            id={`${primaryLabelId}-${option.value}`}
            disabled={disabled}
            variant={variant}
          />
        ))}
      </Flex>
      <FeedbackMessage
        fieldError={fieldError}
        fieldErrorId={fieldErrorId}
        helperMessage={helperMessage}
        helperMessageId={helperMessageId}
      />
    </Flex>
  );
};

export default CheckboxField;
